import type { AppStrings, Language } from '../../../types/strings';

/**
 * Language loading utilities
 * Handles dynamic imports and caching of language files
 */

export class LanguageLoader {
  private static cache = new Map<Language, AppStrings>();
  
  /**
   * Load strings for a specific language
   */
  static async loadLanguage(language: Language): Promise<AppStrings> {
    // Check cache first
    if (this.cache.has(language)) {
      return this.cache.get(language)!;
    }

    try {
      console.log(`[LanguageLoader] Loading strings for: ${language}`);
      
      const [globals, login, dashboard, controlCenter] = await Promise.all([
        import(`./${language}/globals.ts`),
        import(`./${language}/login.ts`),
        import(`./${language}/dashboard.ts`),
        import(`./${language}/controlCenter.ts`),
      ]);

      const loadedStrings: AppStrings = {
        globals: globals.default,
        login: login.default,
        dashboard: dashboard.default,
        controlCenter: controlCenter.default,
      };

      // Cache the loaded strings
      this.cache.set(language, loadedStrings);
      console.log(`[LanguageLoader] Successfully cached strings for: ${language}`);
      
      return loadedStrings;
    } catch (error) {
      console.error(`[LanguageLoader] Failed to load strings for: ${language}`, error);
      
      // If we failed to load a non-English language, fall back to English
      if (language !== 'en') {
        console.log(`[LanguageLoader] Falling back to English for failed language: ${language}`);
        return this.loadLanguage('en'); // Recursive call to load English
      }
      
      // If English also failed, this is a critical error - rethrow
      throw new Error(`Critical: Failed to load English strings - ${error}`);
    }
  }

  /**
   * Pre-load all supported languages in background
   */
  static async preloadAllLanguages(): Promise<void> {
    const supportedLanguages: Language[] = ['en', 'vn'];
    
    const preloadPromises = supportedLanguages.map(async (lang) => {
      if (!this.cache.has(lang)) {
        try {
          await this.loadLanguage(lang);
        } catch (error) {
          console.warn(`[LanguageLoader] Failed to preload: ${lang}`, error);
        }
      }
    });

    await Promise.all(preloadPromises);
    console.log('[LanguageLoader] All languages preloaded');
  }

  /**
   * Check if a language is cached
   */
  static isCached(language: Language): boolean {
    return this.cache.has(language);
  }

  /**
   * Get cached strings (synchronous)
   */
  static getCached(language: Language): AppStrings | null {
    return this.cache.get(language) || null;
  }

  /**
   * Clear cache (useful for development/testing)
   */
  static clearCache(): void {
    this.cache.clear();
    console.log('[LanguageLoader] Cache cleared');
  }
}